import { useMemo, useState } from 'react';
import type { SavedView, ViewDefinition } from '@whinerp/client';
import type { Column } from '@whinerp/ui';

/**
 * A list's saved view, in one place (F-021).
 *
 * Written once and used by every list, because the arithmetic is the part
 * that goes wrong: which of the view and the person wins, what a query has to
 * send for an unsaved change to take effect, and when the cursor stops naming
 * a page of the same list. Copied per screen it would be right on the screen
 * somebody tested and wrong on the next.
 *
 * The three decisions it makes:
 *
 * **What to send.** Only what differs from the saved view. An untouched view
 * produces the request it always did, and `''` says "no grouping" - an absent
 * parameter has to keep meaning "the view decides", or somebody who ungrouped
 * a grouped view would find it regrouping itself on the next page.
 *
 * **When the cursor dies.** A cursor is a position in one order. Changing the
 * view or the grouping changes the order, so the page it names is a page of
 * something else.
 *
 * **Which columns render.** The server's answer, not the draft: it is the
 * server that knows which of a stored view's keys this object still has a
 * column for, and two answers to one question disagree the first time a
 * column is renamed.
 */

export interface SavedViewState {
  active: SavedView | null;
  draft: ViewDefinition;
  /** Query parameters for the list request, already narrowed to what changed. */
  params: { viewId?: string; groupBy?: string; columns?: string };
  onSelect: (view: SavedView | null) => void;
  onChange: (next: ViewDefinition) => void;
  onDiscard: () => void;
  onRemoveFilter: (index: number) => void;
}

export function useSavedView(onCursorReset: () => void): SavedViewState {
  /*
   * Two pieces of state, not one: `draft` is what the list is showing and
   * `active` is what was saved. The difference between them is what the
   * unsaved-changes bar exists to name - collapsing them would mean a change
   * is either instantly saved or instantly lost.
   */
  const [active, setActive] = useState<SavedView | null>(null);
  const [draft, setDraft] = useState<ViewDefinition>({});

  const saved = active?.definition ?? {};
  const params = useMemo(() => {
    const out: { viewId?: string; groupBy?: string; columns?: string } = {};
    // The view's id, not its filter: a stored filter must not be editable in a
    // query string by somebody who may not edit the view. Grouping and columns
    // decide only how rows this person may already see are arranged, so there
    // is nothing there to protect.
    if (active) out.viewId = active.viewId;
    if (draft.groupBy !== saved.groupBy) out.groupBy = draft.groupBy ?? '';
    if (JSON.stringify(draft.columns ?? null) !== JSON.stringify(saved.columns ?? null)) {
      out.columns = (draft.columns ?? []).join(',');
    }
    return out;
  }, [active, draft, saved.groupBy, saved.columns]);

  return {
    active,
    draft,
    params,
    onSelect: (view) => {
      setActive(view);
      setDraft(view?.definition ?? {});
      // A different view is a different filter and a different order, so the
      // page this cursor names is a page of something else.
      onCursorReset();
    },
    onChange: (next) => {
      setDraft(next);
      if (next.groupBy !== draft.groupBy) onCursorReset();
    },
    onDiscard: () => {
      setDraft(saved);
      onCursorReset();
    },
    onRemoveFilter: (index) => {
      const filter = draft.filter as { all?: unknown[] } | undefined;
      if (!filter?.all) return;
      // The filter type is written out to a fixed depth for OpenAPI, and a
      // chip only ever removes from the outermost group - so what comes back
      // is the same shape with one fewer node.
      setDraft({
        ...draft,
        filter: { all: filter.all.filter((_, i) => i !== index) } as ViewDefinition['filter'],
      });
      onCursorReset();
    },
  };
}

/**
 * The columns to render, in the order the server gave them.
 *
 * A key the server names that this table has no column for is dropped rather
 * than rendered blank: it is a view saved before a column was removed, and an
 * empty column with a heading nobody can explain is worse than one fewer.
 */
export function visibleColumns<R>(columns: Column<R>[], shown: string[] | null): Column<R>[] {
  if (shown === null) return columns;
  return shown
    .map((key) => columns.find((column) => column.key === key))
    .filter((column): column is Column<R> => column !== undefined);
}

/**
 * Where a click on a column header takes the sort.
 *
 * One rule, because there were two. The contacts list started a newly clicked
 * column ascending and the leads queue started it descending - the leads rule
 * was written as "the same field and ascending? then descending, else
 * ascending", which is right for the score it was written for and turns every
 * text column into Z-to-A on the first click.
 *
 * A new column starts ascending. The same column flips. A list whose useful
 * order is descending says so in its DEFAULT sort, which is not a click.
 */
export function nextSort(
  current: { field: string; direction: 'asc' | 'desc' },
  field: string,
): { field: string; direction: 'asc' | 'desc' } {
  if (current.field !== field) return { field, direction: 'asc' };
  return { field, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}
